import React, { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Atmos } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';
import { localDateKey } from '../live/screenContext';
import { ScreenHelp } from '../v10/ScreenHelp';
import { syqiHelp } from '../olmps/help/syqiHelp';

interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  updated_at: string;
}

interface Memory {
  id: string;
  content: string;
  category: string;
  created_at: string;
}

const LAST_CONVERSATION_KEY = 'syqi.lastConversationId';

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const SUGGESTIONS = ['What should I focus on?', 'Show me my progress', 'Give me today’s principle'];

export function SyqiTool(_props: ToolProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const conversationId = useRef<string | undefined>(undefined);
  const scrollRef = useRef<ScrollView>(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [memoriesOpen, setMemoriesOpen] = useState(false);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loadingMemories, setLoadingMemories] = useState(false);

  // Resume the last conversation on mount.
  useEffect(() => {
    if (!userId) return;
    (async () => {
      const savedId = await AsyncStorage.getItem(LAST_CONVERSATION_KEY);
      if (savedId) {
        conversationId.current = savedId;
        await loadConversationMessages(savedId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadConversations = useCallback(async () => {
    if (!userId) return;
    setLoadingConvos(true);
    const { data } = await supabase
      .from('chat_conversations')
      .select('id, title, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    setConversations((data || []) as Conversation[]);
    setLoadingConvos(false);
  }, [userId]);

  const loadConversationMessages = async (convoId: string) => {
    setLoadingMessages(true);
    try {
      const { data } = await supabase
        .from('chat_messages')
        .select('id, role, content, created_at')
        .eq('conversation_id', convoId)
        .order('created_at', { ascending: true });
      setMessages(
        ((data || []) as any[]).map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
        }))
      );
    } finally {
      setLoadingMessages(false);
    }
  };

  const openHistory = () => {
    setHistoryOpen(true);
    loadConversations();
  };

  const openConversation = async (convoId: string) => {
    conversationId.current = convoId;
    await AsyncStorage.setItem(LAST_CONVERSATION_KEY, convoId);
    setHistoryOpen(false);
    await loadConversationMessages(convoId);
    requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: false }));
  };

  const startNewChat = async () => {
    conversationId.current = undefined;
    await AsyncStorage.removeItem(LAST_CONVERSATION_KEY);
    setMessages([]);
    setHistoryOpen(false);
  };

  const confirmDeleteConversation = (convoId: string) => {
    Alert.alert(
      'Delete this conversation?',
      'This permanently removes the conversation and all its messages.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => deleteConversation(convoId),
        },
      ]
    );
  };

  const deleteConversation = async (convoId: string) => {
    await supabase.from('chat_conversations').delete().eq('id', convoId);
    setConversations((prev) => prev.filter((c) => c.id !== convoId));
    if (conversationId.current === convoId) {
      conversationId.current = undefined;
      await AsyncStorage.removeItem(LAST_CONVERSATION_KEY);
      setMessages([]);
    }
  };

  const loadMemories = useCallback(async () => {
    if (!userId) return;
    setLoadingMemories(true);
    const { data } = await supabase
      .from('syqi_memories')
      .select('id, content, category, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    setMemories((data || []) as Memory[]);
    setLoadingMemories(false);
  }, [userId]);

  const openMemories = () => {
    setMemoriesOpen(true);
    loadMemories();
  };

  const confirmDeleteMemory = (memoryId: string) => {
    Alert.alert('Delete this memory?', 'This permanently removes the memory.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => deleteMemory(memoryId),
      },
    ]);
  };

  const deleteMemory = async (memoryId: string) => {
    await supabase.from('syqi_memories').delete().eq('id', memoryId);
    setMemories((prev) => prev.filter((m) => m.id !== memoryId));
  };

  const send = async (override?: string) => {
    const message = (override ?? input).trim();
    if (!message || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setSending(true);
    try {
      const data = await apiFetch<{ conversationId: string; message: string }>('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({ message, conversationId: conversationId.current, today: localDateKey() }),
      });
      const isNewConversation = !conversationId.current;
      conversationId.current = data.conversationId;
      await AsyncStorage.setItem(LAST_CONVERSATION_KEY, data.conversationId);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.message }]);
      if (isNewConversation) {
        loadConversations();
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: e instanceof Error ? e.message : 'Something went wrong',
          isError: true,
        },
      ]);
    } finally {
      setSending(false);
      requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
    }
  };

  const filteredConversations = searchTerm
    ? conversations.filter((c) =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : conversations;

  return (
    <Atmos>
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={styles.root}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
    >
      <View style={styles.toolbar}>
        <Pressable style={styles.toolbarButton} onPress={openHistory}>
          <Text style={styles.toolbarButtonText}>History</Text>
        </Pressable>
        <Pressable style={styles.toolbarButton} onPress={startNewChat}>
          <Text style={styles.toolbarButtonText}>+ New</Text>
        </Pressable>
        <Pressable style={styles.toolbarButton} onPress={openMemories}>
          <Text style={styles.toolbarButtonText}>Memories</Text>
        </Pressable>
        <ScreenHelp help={syqiHelp} style={styles.toolbarHelp} />
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: true })}
      >
        {loadingMessages && (
          <View style={styles.centerRow}>
            <ActivityIndicator size="small" color={t.ivorySoft} />
          </View>
        )}
        {!loadingMessages && messages.length === 0 && (
          <Text style={styles.placeholder}>Ask SyQi anything about your life system.</Text>
        )}
        {!loadingMessages && messages.length === 0 && (
          <View style={styles.suggest}>
            {SUGGESTIONS.map((s) => (
        <Pressable key={s} onPress={() => void send(s)} style={styles.chip}>
          <Text style={styles.chipText}>{s}</Text>
        </Pressable>
            ))}
          </View>
        )}
        {messages.map((m, i) => (
          <View
            key={m.id ?? i}
            style={[
              styles.bubble,
              m.role === 'user' ? styles.userBubble : styles.assistantBubble,
              m.isError && styles.errorBubble,
            ]}
          >
            <Text style={[styles.bubbleText, m.role === 'user' && styles.userBubbleText, m.isError && styles.errorText]}>{m.content}</Text>
          </View>
        ))}
        {sending && (
          <View style={[styles.bubble, styles.assistantBubble, styles.thinking]}>
            <ActivityIndicator size="small" color={t.ivorySoft} />
            <Text style={styles.thinkingText}>SyQi is thinking…</Text>
          </View>
        )}
      </ScrollView>
      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          placeholder="Message SyQi"
          placeholderTextColor="#8a8378"
          value={input}
          onChangeText={setInput}
          onSubmitEditing={() => send()}
          returnKeyType="send"
        />
        <Pressable style={styles.sendButton} onPress={() => send()} disabled={sending}>
          <Text style={styles.sendText}>Send</Text>
        </Pressable>
      </View>

      {/* Conversation history */}
      <Modal visible={historyOpen} transparent animationType="slide" onRequestClose={() => setHistoryOpen(false)}>
        <View style={styles.backdrop}>
          <View style={styles.sheet}>
            <Atmos>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Conversations</Text>
              <Pressable onPress={() => setHistoryOpen(false)} style={styles.close}>
                <Text style={styles.closeText}>✕</Text>
              </Pressable>
            </View>
            <TextInput
              style={styles.searchInput}
              placeholder="Search conversations..."
              placeholderTextColor={t.ivoryFaint}
              value={searchTerm}
              onChangeText={setSearchTerm}
            />
            <Pressable style={styles.newChatRow} onPress={startNewChat}>
              <Text style={styles.newChatText}>+ New Chat</Text>
            </Pressable>
            <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
              {loadingConvos && (
                <View style={styles.centerRow}>
                  <ActivityIndicator size="small" color={t.ivorySoft} />
                </View>
              )}
              {!loadingConvos && filteredConversations.length === 0 && (
                <Text style={styles.placeholder}>
                  {searchTerm ? 'No matches.' : 'No conversations yet.'}
                </Text>
              )}
              {filteredConversations.map((c) => (
                <Pressable
                  key={c.id}
                  style={[
                    styles.convoRow,
                    conversationId.current === c.id && styles.convoRowActive,
                  ]}
                  onPress={() => openConversation(c.id)}
                  onLongPress={() => confirmDeleteConversation(c.id)}
                >
                  <View style={styles.convoRowText}>
                    <Text style={styles.convoTitle} numberOfLines={1}>
                      {c.title || 'Untitled conversation'}
                    </Text>
                    <Text style={styles.convoDate}>{formatDate(c.updated_at)}</Text>
                  </View>
                  <Pressable
                    hitSlop={8}
                    style={styles.deleteButton}
                    onPress={() => confirmDeleteConversation(c.id)}
                  >
                    <Text style={styles.deleteButtonText}>Delete</Text>
                  </Pressable>
                </Pressable>
              ))}
            </ScrollView>
            </Atmos>
          </View>
        </View>
      </Modal>

      {/* Memories */}
      <Modal visible={memoriesOpen} transparent animationType="slide" onRequestClose={() => setMemoriesOpen(false)}>
        <View style={styles.backdrop}>
          <View style={styles.sheet}>
            <Atmos>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>SyQi's Memory</Text>
              <Pressable onPress={() => setMemoriesOpen(false)} style={styles.close}>
                <Text style={styles.closeText}>✕</Text>
              </Pressable>
            </View>
            <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
              {loadingMemories && (
                <View style={styles.centerRow}>
                  <ActivityIndicator size="small" color={t.ivorySoft} />
                </View>
              )}
              {!loadingMemories && memories.length === 0 && (
                <Text style={styles.placeholder}>
                  No memories yet. SyQi will automatically remember important things from your
                  conversations.
                </Text>
              )}
              {memories.map((mem) => (
                <View key={mem.id} style={styles.memoryCard}>
                  <View style={styles.memoryHeader}>
                    <Text style={styles.memoryCategory}>{mem.category}</Text>
                    <Pressable hitSlop={8} onPress={() => confirmDeleteMemory(mem.id)}>
                      <Text style={styles.deleteButtonText}>Delete</Text>
                    </Pressable>
                  </View>
                  <Text style={styles.memoryContent}>{mem.content}</Text>
                  <Text style={styles.memoryDate}>
                    {new Date(mem.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Text>
                </View>
              ))}
            </ScrollView>
            </Atmos>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
    root: { flex: 1 },
    sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 14 },
    sheetTitle: { fontSize: 18, letterSpacing: 2.5, color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
    sheet: {
      borderTopLeftRadius: 28,
      borderTopRightRadius: 28,
      borderWidth: 1,
      borderColor: t.navyLine,
      overflow: 'hidden',
      height: 520,
      maxHeight: '85%',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
    },
    title: { fontSize: 18, letterSpacing: 2.5, color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    close: { position: 'absolute', right: 14, top: 12, padding: 4 },
    closeText: { fontSize: 18, color: t.ivorySoft },
    searchInput: {
      marginHorizontal: 14,
      marginBottom: 8,
      backgroundColor: c.inputFill,
      borderRadius: radii.pill,
      paddingHorizontal: 14,
      paddingVertical: 8,
      fontSize: 14,
      color: t.ivory,
      borderWidth: 1,
      borderColor: t.navyLine,
    },
    toolbar: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: 14,
      marginBottom: 6,
    },
    toolbarButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: radii.pill,
      paddingVertical: 8,
      alignItems: 'center',
    },
    toolbarHelp: { alignSelf: 'center' },
    toolbarButtonText: { fontSize: 13, fontWeight: '700', color: t.goldBright },
    scroll: { flex: 1 },
    scrollContent: { padding: 16, gap: 10 },
    suggest: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
    chip: { borderWidth: 1, borderColor: t.navyLine, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7 },
    chipText: { color: t.goldBright, fontSize: 13 },
    centerRow: { paddingVertical: 24, alignItems: 'center' },
    placeholder: { color: t.ivoryFaint, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },
    bubble: {
      maxWidth: '86%',
      borderRadius: 18,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderWidth: 1,
    },
    userBubble: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold, boxShadow: `0 4px 14px ${t.goldGlow}` },
    assistantBubble: { alignSelf: 'flex-start', backgroundColor: t.navyRaised, borderColor: t.navyLine },
    errorBubble: { borderColor: t.lapsed },
    bubbleText: { fontSize: 15, lineHeight: 21, color: t.ivory },
    userBubbleText: { color: t.onGold },
    errorText: { color: t.lapsed },
    thinking: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    thinkingText: { color: t.ivorySoft, fontStyle: 'italic' },
    inputRow: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: 16,
      paddingVertical: 10,
      paddingBottom: Platform.OS === 'ios' ? 24 : 10,
    },
    input: {
      flex: 1,
      height: 46,
      borderRadius: 23,
      backgroundColor: '#ffffff',
      paddingHorizontal: 16,
      color: '#111',
      fontSize: 15,
      borderWidth: 1,
      borderColor: t.gold,
    },
    sendButton: {
      boxShadow: `0 4px 14px ${t.goldGlow}`,
      backgroundColor: t.gold,
      borderRadius: 23,
      height: 46,
      paddingHorizontal: 18,
      alignItems: 'center',
      justifyContent: 'center',
    },
    sendText: { fontWeight: '900', color: t.onGold },

    sheetScroll: { flex: 1 },
    sheetScrollContent: { padding: 14, gap: 8, paddingBottom: 24 },
    newChatRow: {
      boxShadow: `0 4px 14px ${t.goldGlow}`,
      marginHorizontal: 14,
      marginBottom: 8,
      backgroundColor: t.gold,
      borderRadius: radii.pill,
      paddingVertical: 10,
      alignItems: 'center',
    },
    newChatText: { fontWeight: '800', color: t.onGold },
    convoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 14,
      paddingHorizontal: 14,
      paddingVertical: 12,
    },
    convoRowActive: { backgroundColor: c.lime, borderColor: t.gold },
    convoRowText: { flex: 1, minWidth: 0, gap: 2 },
    convoTitle: { fontSize: 15, color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    convoDate: { fontSize: 12, color: t.ivoryFaint },
    deleteButton: { paddingHorizontal: 8, paddingVertical: 4 },
    deleteButtonText: { fontSize: 13, fontWeight: '700', color: t.lapsed },

    memoryCard: {
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 14,
      padding: 12,
      gap: 4,
    },
    memoryHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    memoryCategory: {
      fontFamily: DISPLAY_FONT,
      fontSize: 11,
      color: t.goldBright,
      letterSpacing: 1.5,
      textTransform: 'uppercase',
    },
    memoryContent: { fontSize: 14, color: t.ivory, lineHeight: 20 },
    memoryDate: { fontSize: 12, color: t.ivoryFaint },
  }),
);
